import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect, useRef, useState } from 'react';
import { useAuthStore } from '@/stores/auth';
import { api } from '@/lib/api';
import { hasPlaceholderPermission } from '@/lib/permissions';
import { Loader2 } from 'lucide-react';
import { HyggeLogo } from '@/components/icons/hygge-logo';

export const Route = createFileRoute('/auth/callback')({
  component: AuthCallbackPage,
});

function AuthCallbackPage() {
  const navigate = useNavigate();
  const { setUser } = useAuthStore();
  const [error, setError] = useState<string | null>(null);
  const isProcessing = useRef(false);

  useEffect(() => {
    // Prevent double execution in React Strict Mode
    if (isProcessing.current) return;
    isProcessing.current = true;

    const handleCallback = async () => {
      const searchParams = new URLSearchParams(window.location.search);
      const errorCode = searchParams.get('error');

      // Handle error from backend
      if (errorCode) {
        navigate({
          to: '/login',
          search: { error: errorCode },
        });
        return;
      }

      try {
        // Cookies are already set by the backend
        // Just fetch user data to verify auth worked
        const response = await api.get('/auth/me');
        const userData = response.data;

        // Update auth store
        setUser(userData);

        // Clean URL and redirect — placeholder users go to /placeholder (no employee/org data loaded)
        window.history.replaceState({}, '', '/');
        navigate({ to: hasPlaceholderPermission(userData) ? '/placeholder' : '/' });
      } catch {
        setError('Failed to complete authentication. Please try again.');

        setTimeout(() => {
          navigate({
            to: '/login',
            search: { error: 'GOOGLE_AUTH_FAILED' },
          });
        }, 2000);
      }
    };

    handleCallback();
  }, [navigate, setUser]);

  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-6 bg-background p-4">
      <HyggeLogo className="h-10 opacity-80" />

      {error ? (
        <div className="flex flex-col items-center gap-3">
          <div className="text-sm text-destructive">{error}</div>
          <div className="text-xs text-muted-foreground">
            Redirecting to login...
          </div>
        </div>
      ) : (
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          <p className="text-sm text-muted-foreground">
            Completing sign in...
          </p>
        </div>
      )}
    </div>
  );
}
